import React from "react";
import styled from "styled-components";
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import dayjs from 'dayjs';
import { getCourseTimeZone, getTimeZoneOptions, shiftToTimeZone, toZonedDayjs } from '../../utils/courseSchedule';

const CourseForm = ({ course, onInputChange, onDateChange, onSubmit, onCancel, submitButtonText, isSubmitting }) => {
    const timeZone = getCourseTimeZone(course);
    const startVal = toZonedDayjs(course.startDate, timeZone);
    const endVal = toZonedDayjs(course.endDate, timeZone);

    // Keep the picked wall-clock times when switching zones
    const handleTimeZoneChange = (e) => {
        const nextZone = e.target.value;
        onDateChange("startDate", shiftToTimeZone(course.startDate, timeZone, nextZone));
        onDateChange("endDate", shiftToTimeZone(course.endDate, timeZone, nextZone));
        onInputChange({ target: { name: "timeZone", value: nextZone } });
    };
    return (
        <FormContainer>
            <Input
                type="text"
                name="name"
                value={course.name}
                onChange={onInputChange}
                placeholder="Course Name"
            />
            <Input
                type="text"
                name="description"
                value={course.description}
                onChange={onInputChange}
                placeholder="Course Description"
            />
            <TimeZoneSelect
                name="timeZone"
                value={timeZone}
                onChange={handleTimeZoneChange}
                aria-label="Course time zone"
                title="Course time zone"
            >
                {getTimeZoneOptions().map((tz) => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
            </TimeZoneSelect>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <StyledDatePickerWrapper>
                    <DateTimePicker
                        label="Course Start"
                        value={startVal}
                        timezone={timeZone}
                        referenceDate={dayjs().tz(timeZone).startOf('day')}
                        onChange={(v) =>
                            onDateChange("startDate", v ? v.toDate() : null)
                        }
                        slotProps={{ popper: { disablePortal: true } }}
                    />

                </StyledDatePickerWrapper>
                <StyledDatePickerWrapper>
                    <DateTimePicker
                        label="Course End (optional)"
                        value={endVal}
                        timezone={timeZone}
                        referenceDate={dayjs().tz(timeZone).endOf('day')}
                        minDateTime={startVal}
                        onChange={(v) =>
                            onDateChange("endDate", v ? v.toDate() : null)
                        }
                        slotProps={{
                            popper: { disablePortal: true },
                            field: {
                                clearable: true,
                                onClear: () => onDateChange("endDate", null),
                            },
                            actionBar: {
                                // Listing actions replaces MUI's default, so keep OK alongside Clear
                                actions: ['clear', 'accept'],
                                sx: {
                                    // style all action buttons
                                    '& .MuiButton-root': {
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        borderRadius: 1.5,
                                        color: '#9333ea',
                                        '&:hover': { backgroundColor: 'rgba(147,51,234,0.08)' },
                                    },
                                },
                            },
                        }}
                        // Let the field be cleared to keep it optional:
                        reduceAnimations
                    />
                </StyledDatePickerWrapper>
            </LocalizationProvider>
            <ButtonGroup>
                <SubmitButton onClick={onSubmit} disabled={isSubmitting}>
                    {isSubmitting ? "Saving..." : submitButtonText}
                </SubmitButton>
                <CancelButton onClick={onCancel}>Cancel</CancelButton>
            </ButtonGroup>
        </FormContainer>
    );
};

const FormContainer = styled.div`
    background-color: white;
    border-radius: 12px;
    padding: 10px;
    width: 300px;
    height: 266px; /* same as CourseCard */
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    //align-items: center;
`;

const Input = styled.input`
    box-sizing: border-box;
    height: 32px;
    padding: 6px 8px;
    margin: 3px 0;
    font-size: 14px;
    border-radius: 5px;
    border: 1px solid #ddd;
    width: 100%;
    &:hover {
        border-color: #b189d5; /* match DatePicker hover */
    }

    &:focus {
        outline: none;
        border-color: #9333ea; /* match DatePicker focused */
        box-shadow: 0 0 0 1px #9333ea; /* optional glow */
    }
}`;

// Custom chevron so it lines up with the date pickers' calendar icons
// (icon centre ~22px from the right edge, MUI's icon grey).
const TimeZoneSelect = styled(Input).attrs({ as: "select" })`
    font-family: inherit;
    appearance: none;
    -webkit-appearance: none;
    padding-right: 36px;
    background: white url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1.5l5 5 5-5' fill='none' stroke='%23757575' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") no-repeat right 16px center;
    text-overflow: ellipsis;
    cursor: pointer;
`;

const StyledDatePickerWrapper = styled.div`
    margin: 3px 0;
    width: 100%;
    .MuiTextField-root {
        //height: 1px;
        width: 100%;
    }
    .MuiInputBase-root {
        height: 40px;
    }
    .MuiInputLabel-root {
        margin-top: -5px;
        background-color: #ffffffff;
        font-family: "Inter", sans-serif;
        font-size: 14px;
    }
    .MuiInputLabel-root.Mui-focused {
        color: #9333ea;
        font-weight: 500;
    }

    /* border */
    .MuiOutlinedInput-root {
        fieldset {
        border-color: #ccc;
        }
        &:hover fieldset {
        border-color: #b189d5ff;
        }
        &.Mui-focused fieldset {
        border-color: #9333ea;
        }
    }

    /* selected highlight in calendar */
    .MuiPickersDay-root.Mui-selected {
        background-color: #9333ea; /* fill */
        color: #fff;               /* text */
    }

    /* selected + hover */
    .MuiPickersDay-root.Mui-selected:hover {
    background-color: #a074cb;
    }
    /* plain hover (not selected) */
    .MuiPickersDay-root:hover {
    background-color: rgba(177, 137, 213, 0.15);
    }

    /* Any selected day */
    .MuiPickersDay-root.Mui-selected {
    background-color: #9333ea;
    color: #fff;
    }

    .MuiPickersDay-root.Mui-selected:not(.MuiPickersDay-today) { 
    background-color: #9333ea; 
    color: #fff; 
    }

    /* Selected + today (override MUI's blue) */
    .MuiPickersDay-root.Mui-selected.MuiPickersDay-today {
    background-color: #9333ea; /* same purple as selected */
    color: #fff;
    border: 2px solid #9333ea; /* keep the today ring if you want */
    }

    /* Today but not selected */
    .MuiPickersDay-root.MuiPickersDay-today:not(.Mui-selected) {
    border: 2px solid #9333ea;
    background-color: transparent;
    }

    /* Selected year */
    .MuiYearCalendar-root .MuiPickersYear-yearButton.Mui-selected {
    background-color: #9333ea;
    color: #fff;
    }

    /* Hover on any year button */
    .MuiYearCalendar-root .MuiPickersYear-yearButton:hover {
    background-color: #d5c1e6ff;
    }

    /* Today outline in year view */
    .MuiYearCalendar-root .MuiPickersYear-yearButton.MuiPickersYear-today {
    border: 2px solid #9333ea;
    }

    /* Selected hour / minute / AM-PM in the time columns */
    .MuiMultiSectionDigitalClockSection-item.Mui-selected,
    .MuiMultiSectionDigitalClockSection-item.Mui-selected:hover {
    background-color: #9333ea;
    color: #fff;
    }

    /* Start picker's default OK button */
    .MuiPickersLayout-actionBar .MuiButton-root {
    color: #9333ea;
    }

`;

const ButtonGroup = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-top: 3px;
    gap: 10px;
`;


const BaseButton = styled.button`
  width: 100%;
  font-weight: bold;
  padding: 8px;
  border-radius: 0.75rem;
  color: white;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease-in-out;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${({ $ringColor }) => $ringColor},
      0 0 0 4px rgba(0, 0, 0, 0.1);
  }
`;

export const SubmitButton = styled(BaseButton)`
  background-color: #22c55e;

  &:hover {
    background-color: #16a34a;
  }

  &:focus {
    box-shadow: 0 0 0 2px #22c55e50;
  }
`;

export const CancelButton = styled(BaseButton)`
  background-color: #ef4444;

  &:hover {
    background-color: #dc2626;
  }

  &:focus {
    box-shadow: 0 0 0 2px #ef444450;
  }
`;

export default CourseForm;